/**
 * Base Wizard Component
 * Alpine.js component for wizard state management
 */

// Import draftSave - will be loaded dynamically to avoid circular dependencies
// import { draftSave } from '../utils/draft-save.js';

export function wizardComponent(config = {}) {
    return {
        // Configuration
        totalSteps: config.totalSteps || 5,
        currentStep: config.currentStep || 1,
        draftKey: config.draftKey || null,
        autoSaveInterval: config.autoSaveInterval || 30000, // 30 seconds
        
        // State
        formData: config.formData || {},
        errors: {},
        isSubmitting: false,
        showSuccess: false,
        successMessage: '',
        
        // Auto-save cleanup
        autoSaveCleanup: null,
        
        // Initialization
        async init() {
            // Load draft if available (dynamic import to avoid circular dependencies)
            if (this.draftKey) {
                const { draftSave } = await import('../utils/draft-save.js');
                const draft = draftSave.load(this.draftKey);
                if (draft) {
                    this.formData = { ...this.formData, ...draft };
                    // Optionally show a notification that draft was loaded
                    this.$nextTick(() => {
                        if (this.onDraftLoaded) {
                            this.onDraftLoaded(draft);
                        }
                    });
                }
                
                // Start auto-save if enabled
                this.autoSaveCleanup = draftSave.autoSave(
                    this.draftKey,
                    () => this.getFormData(),
                    this.autoSaveInterval
                );
            }
            
            // Initialize step
            this.goToStep(this.currentStep);
        },
        
        // Cleanup on destroy
        destroy() {
            if (this.autoSaveCleanup) {
                this.autoSaveCleanup();
            }
        },
        
        // Get current form data (to be overridden by child components)
        getFormData() {
            return this.formData;
        },
        
        // Navigate to specific step
        goToStep(step) {
            if (step < 1 || step > this.totalSteps) {
                return false;
            }
            
            // Validate current step before moving
            if (step > this.currentStep && !this.validateStep(this.currentStep)) {
                return false;
            }
            
            this.currentStep = step;
            this.clearErrors();
            
            // Scroll to top of wizard
            this.$nextTick(() => {
                const wizardContainer = this.$el.querySelector('.wizard-container');
                if (wizardContainer) {
                    wizardContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
            
            return true;
        },
        
        // Go to next step
        nextStep() {
            if (this.currentStep < this.totalSteps) {
                return this.goToStep(this.currentStep + 1);
            }
            return false;
        },
        
        // Go to previous step
        previousStep() {
            if (this.currentStep > 1) {
                return this.goToStep(this.currentStep - 1);
            }
            return false;
        },
        
        // Validate current step (to be overridden by child components)
        validateStep(step) {
            // Default: no validation
            // Child components should override this
            return true;
        },
        
        // Clear errors
        clearErrors() {
            this.errors = {};
        },
        
        // Set errors
        setErrors(errors) {
            this.errors = errors || {};
        },
        
        // Save draft manually
        async saveDraft() {
            if (this.draftKey) {
                const { draftSave } = await import('../utils/draft-save.js');
                const data = this.getFormData();
                return draftSave.save(this.draftKey, data);
            }
            return false;
        },
        
        // Clear draft
        async clearDraft() {
            if (this.draftKey) {
                const { draftSave } = await import('../utils/draft-save.js');
                return draftSave.clear(this.draftKey);
            }
            return false;
        },
        
        // Submit form (to be overridden by child components)
        async submit() {
            this.isSubmitting = true;
            this.clearErrors();
            
            try {
                // Validate all steps
                for (let step = 1; step <= this.totalSteps; step++) {
                    if (!this.validateStep(step)) {
                        this.goToStep(step);
                        const error = new Error('Please complete all required fields');
                        error.errors = { general: ['لطفاً تمام فیلدهای الزامی را تکمیل کنید'] };
                        throw error;
                    }
                }
                
                // Call child's submit handler
                if (this.onSubmit) {
                    await this.onSubmit(this.getFormData());
                }
                
                // Clear draft on successful submit
                if (this.draftKey) {
                    await this.clearDraft();
                }
                
            } catch (error) {
                console.error('Form submission error:', error);
                if (error.errors) {
                    this.setErrors(error.errors);
                } else {
                    this.setErrors({ general: [error.message || 'خطا در ارسال فرم'] });
                }
            } finally {
                this.isSubmitting = false;
            }
        },
        
        // Show success message
        showSuccessMessage(message) {
            this.showSuccess = true;
            this.successMessage = message;
        },
        
        // Check if step is completed
        isStepCompleted(step) {
            return step < this.currentStep;
        },
        
        // Check if step is active
        isStepActive(step) {
            return step === this.currentStep;
        },
        
        // Get step progress percentage
        getProgress() {
            return (this.currentStep / this.totalSteps) * 100;
        },
        
        // Get step class for wizard-steps component
        getStepClass(stepNum) {
            if (this.currentStep === stepNum) {
                return 'bg-blue-600 text-white shadow-lg scale-110';
            } else if (this.currentStep > stepNum) {
                return 'bg-green-500 text-white';
            } else {
                return 'bg-gray-200 text-gray-600';
            }
        },
        
        // Get step label class
        getStepLabelClass(stepNum) {
            return this.currentStep === stepNum ? 'text-blue-600 font-bold' : 'text-gray-600';
        },
        
        // Get connector line class
        getConnectorClass(stepNum) {
            return this.currentStep > stepNum ? 'bg-green-500' : 'bg-gray-200';
        }
    };
}

